/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Permission Context — provides can() and canAny() for RBAC enforcement.
 *
 * Reads permissions from the /auth/me API response (via useCurrentUser).
 * Permissions are loaded once at login and cached by TanStack Query.
 * can() is an O(1) Set.has() lookup — zero API calls, zero re-renders.
 *
 * Usage:
 *   const { can, canAny } = usePermissions();
 *   if (can("submissions:create")) { ... }
 *   if (canAny("admin:manage_users", "admin:edit_config")) { ... }
 */

import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useCurrentUser } from "@/api/hooks/useUsers";
import { DEMO_MODE } from "@/api/core";

interface PermissionContextValue {
  /** Check if user has a specific permission */
  can: (code: string) => boolean;
  /** Check if user has ANY of the given permissions */
  canAny: (...codes: string[]) => boolean;
  /** Raw permission codes array */
  permissions: string[];
  /** Internal role name from the user table (e.g. "system_admin", "state_admin"). */
  role: string;
  /**
   * Exact role check — NOT bypassed by super-user. Use for pages restricted to one specific role
   * (e.g. system_admin-only config) where the broader super-user grant would over-expose.
   */
  hasRole: (roleName: string) => boolean;
  /** True once permissions have loaded from API */
  isLoaded: boolean;
}

const PermissionContext = createContext<PermissionContextValue>({
  can: () => false,
  canAny: () => false,
  permissions: [],
  role: "",
  hasRole: () => false,
  isLoaded: false,
});

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { data: currentUser, isSuccess } = useCurrentUser();

  const value = useMemo<PermissionContextValue>(() => {
    // Demo mode (E2E / local dev without backend): grant all permissions immediately
    // so RequirePermission gates don't block rendering while waiting for /auth/me.
    if (DEMO_MODE) {
      return {
        can: () => true,
        canAny: () => true,
        permissions: [],
        role: "state_admin",
        hasRole: () => true,
        isLoaded: true,
      };
    }

    const perms: string[] = Array.isArray(currentUser?.permissions) ? currentUser.permissions : [];
    const permSet = new Set(perms);
    // Check both top-level and primaryRole.isSuperUser (demo user sets it on primaryRole)
    const isSuperUser = currentUser?.isSuperUser ?? currentUser?.primaryRole?.isSuperUser ?? false;
    const role = currentUser?.role ?? currentUser?.primaryRole?.name ?? "";

    return {
      can: (code: string) => isSuperUser || permSet.has(code),
      canAny: (...codes: string[]) => isSuperUser || codes.some((c) => permSet.has(c)),
      permissions: perms,
      role,
      hasRole: (roleName: string) => role === roleName,
      isLoaded: isSuccess,
    };
  }, [
    currentUser?.permissions,
    currentUser?.isSuperUser,
    currentUser?.primaryRole?.isSuperUser,
    currentUser?.role,
    currentUser?.primaryRole?.name,
    isSuccess,
  ]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook must be co-located with its context
export function usePermissions() {
  return useContext(PermissionContext);
}
